import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Tabs, TabsList, TabsContent, EmptyState } from "@nexus/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { MarketplaceTab } from "@/routes/_authenticated/marketplace.index";
import { vendorsQueryOptions, connectionsQueryOptions, ordersQueryOptions } from "@/lib/queries";
import { VendorCard, VendorCardSkeleton } from "../components/VendorCard";
import { ConnectWizard } from "../components/ConnectWizard/ConnectWizard";
import { useConnectWizardStore } from "../store/connectWizard.store";
import { MyConnectionsTab } from "../components/MyConnectionsTab";
import { OrdersTab } from "../components/OrdersTab";

const SKELETON_COUNT = 6;

function CatalogTab() {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  const vendorsQuery = useQuery(vendorsQueryOptions(activeTenantId));
  const connectionsQuery = useQuery(connectionsQueryOptions(activeTenantId));

  const wizardVendorId = useConnectWizardStore((s) => s.vendorId);
  const wizardOpen = useConnectWizardStore((s) => s.open);
  const closeWizard = useConnectWizardStore((s) => s.closeWizard);

  const connectedVendorIds = new Set(
    connectionsQuery.isSuccess ? connectionsQuery.data.map((c) => c.vendorId) : [],
  );

  if (!activeTenantId) return null;

  // Gate on connections too: until they resolve, connectedVendorIds is empty and
  // already-connected vendors would briefly reappear in the catalog.
  if (vendorsQuery.isLoading || connectionsQuery.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <VendorCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (vendorsQuery.isError) {
    return (
      <div className="py-12 text-center">
        <p className="font-medium">{t("marketplace.catalog.errorTitle")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("errors.tryAgain", "Please try again.")}
        </p>
      </div>
    );
  }

  const vendors = vendorsQuery.data ?? [];

  if (vendors.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.catalog.empty")}
        description={t("marketplace.catalog.emptyDescription")}
      />
    );
  }

  // Connected vendors live in the My Connections tab; the catalog only surfaces
  // vendors the tenant can still connect to, so the same vendor never shows twice.
  const availableVendors = vendors.filter((v) => !connectedVendorIds.has(v.id));

  if (availableVendors.length === 0) {
    return (
      <EmptyState
        title={t("marketplace.catalog.allConnectedTitle")}
        description={t("marketplace.catalog.allConnectedDescription")}
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {availableVendors.map((vendor) => (
          <VendorCard key={vendor.id} vendor={vendor} />
        ))}
      </div>

      {wizardVendorId && (
        <ConnectWizard
          vendorId={wizardVendorId}
          tenantId={activeTenantId}
          open={wizardOpen}
          onClose={closeWizard}
        />
      )}
    </>
  );
}

interface MarketplacePageProps {
  tab: MarketplaceTab;
  onTabChange: (tab: MarketplaceTab) => void;
  highlightOrderId?: string;
}

export function MarketplacePage({ tab, onTabChange, highlightOrderId }: MarketplacePageProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);

  const connectionsCount = useQuery(connectionsQueryOptions(activeTenantId)).data?.length ?? 0;
  const ordersCount = useQuery(ordersQueryOptions(activeTenantId)).data?.length ?? 0;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("marketplace.title")}
        </h1>
        <p className="max-w-prose text-pretty text-sm text-muted-foreground">
          {t("marketplace.subtitle")}
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => onTabChange(v as MarketplaceTab)}>
        <TabsList
          variant="line"
          options={[
            { value: "catalog", label: t("marketplace.tabs.catalog") },
            {
              value: "connections",
              label: t("marketplace.tabs.connections"),
              badge: connectionsCount > 0 ? connectionsCount : undefined,
            },
            {
              value: "orders",
              label: t("marketplace.tabs.orders"),
              badge: ordersCount > 0 ? ordersCount : undefined,
            },
          ]}
        />
        <TabsContent value="catalog" className="mt-6">
          <CatalogTab />
        </TabsContent>
        <TabsContent value="connections" className="mt-6">
          {activeTenantId && (
            <MyConnectionsTab
              tenantId={activeTenantId}
              onBrowseCatalog={() => onTabChange("catalog")}
            />
          )}
        </TabsContent>
        <TabsContent value="orders" className="mt-6">
          {activeTenantId && (
            <OrdersTab tenantId={activeTenantId} highlightOrderId={highlightOrderId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
